import React, { useState, useRef, useEffect } from 'react';
import { useFilters } from '../context/FiltersContext';
import { useKommunData } from '../hooks/useKommunData';
import { useDashboardStore, useDashboardActions } from '../stores/useDashboardStore';
import { kpiMeta } from '../config/kpi-config';
import { DashboardLoadingSkeleton } from './feedback/Skeleton';
import { FancyLoader } from './feedback/FancyLoader';
import { useDuckDB } from '../context/LazyDuckDBContext';
import Card from './Card';
import KpiGroupPanel from './KpiGroupPanel';
import SectionAccordion from './Layout/SectionAccordion';
import LazyAnalysisPanel from './LazyAnalysisPanel';
import FullScreenModal from './FullScreenModal';
import KommunSummaryHero from './dashboard/KommunSummaryHero';
import StatisticalAnalysisSummary from './StatisticalAnalysisSummary';
import HeroBanner from './dashboard/HeroBanner';
import { useIsDesktop } from '../hooks/useIsDesktop';

const KommunDashboardView: React.FC = () => {
  // 1. ALL HOOKS AT THE VERY TOP
  const { value: globalFilters, updateFilter } = useFilters();
  const selectedKpiId = useDashboardStore((state) => state.selectedKpiId);
  const isMobileSheetOpen = useDashboardStore((state) => state.isMobileSheetOpen);
  const { setSelectedKpiId, setIsMobileSheetOpen, setIsAnalysisOpen } = useDashboardActions();
  const deepDiveRef = useRef<HTMLDivElement>(null);
  const { query } = useDuckDB();
  const [kommunName, setKommunName] = useState<string | null>(null);
  const [showSecondarySections, setShowSecondarySections] = useState(false);
  const isDesktop = useIsDesktop();

  // Ensure page is at top on mount or when key filters change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [globalFilters.kommunId]);

  // Defer secondary sections to prioritize initial load
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSecondarySections(true);
    }, 200);
    return () => clearTimeout(timer);
  }, []);

  const selectedYear = globalFilters.year ?? 2024;
  const activeKpi = selectedKpiId || 'N15504';

  const {
    loading,
    error,
    kpiData,
    trendData,
    distributionData,
    driverData,
    insightData,
    allKpiData,
    dynamicTrendData,
    dynamicDistributionData,
    correlationData,
    riketData,
    studentCountStatus
  } = useKommunData(
    globalFilters.kommunId,
    selectedYear,
    activeKpi,
    globalFilters.huvudmanId || null,
    globalFilters.skolaId || null,
    globalFilters.analyticalFilters
  );

  useEffect(() => {
    const nextKpi = globalFilters.kpiId && globalFilters.kpiId !== 'null'
      ? globalFilters.kpiId
      : null;
    if (nextKpi && nextKpi !== selectedKpiId) {
      setSelectedKpiId(nextKpi);
    }
  }, [globalFilters.kpiId, selectedKpiId, setSelectedKpiId]);

  const latestYear = kpiData?.latestYear ?? selectedYear;

  // Sync global filters with dashboard store
  useEffect(() => {
    const currentState = useDashboardStore.getState();
    const needsUpdate =
      currentState.municipalityId !== globalFilters.kommunId ||
      currentState.year !== (globalFilters.year ?? 2024) ||
      currentState.governance !== (globalFilters.huvudmanId || null);

    if (needsUpdate) {
      useDashboardStore.setState({
        municipalityId: globalFilters.kommunId,
        year: selectedYear,
        governance: globalFilters.huvudmanId || null
      });
    }
  }, [globalFilters.kommunId, globalFilters.year, globalFilters.huvudmanId, selectedYear]);

  // Resolve municipality name for display
  useEffect(() => {
    let cancelled = false;
    async function loadName() {
      if (!globalFilters.kommunId) { setKommunName(null); return; }
      try {
        const rows = await query<{ name: string }[]>(
          `SELECT municipality_name AS name
           FROM gold_kpi_muni_year
           WHERE municipality_id = :id AND municipality_name IS NOT NULL
           ORDER BY year DESC
           LIMIT 1`,
          { id: globalFilters.kommunId }
        );
        if (!cancelled) setKommunName(rows[0]?.name ?? globalFilters.kommunId);
      } catch {
        if (!cancelled) setKommunName(globalFilters.kommunId);
      }
    }
    loadName();
    return () => { cancelled = true; };
  }, [query, globalFilters.kommunId]);

  const handleKpiSelect = (kpiId: string) => {
    setSelectedKpiId(kpiId);
    if (globalFilters.kpiId !== kpiId) {
      updateFilter('kpiId', kpiId);
    }
    setIsAnalysisOpen(true);

    const isMobile = window.innerWidth < 1280;
    if (isMobile) {
      setIsMobileSheetOpen(true);
    } else {
      if (deepDiveRef.current) {
        deepDiveRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }
  };

  // 2. EARLY RETURNS (After all hooks)
  if (!globalFilters.kommunId) {
    return (
      <div className="space-y-6 pt-20">
        <main className="mx-auto max-w-[1200px] p-4 md:p-6">
          <Card>
            <div className="text-center p-8">
              <h3 className="text-lg font-medium text-foreground mb-2">Välj en kommun</h3>
              <p className="text-muted-foreground">Använd filtret ovan för att börja analysen.</p>
            </div>
          </Card>
        </main>
      </div>
    );
  }

  if (loading && Object.keys(allKpiData).length === 0) {
    return <DashboardLoadingSkeleton />;
  }

  if (error) {
    return (
      <div className="space-y-6 pt-20">
        <main className="w-full max-w-[1400px] mx-auto p-4 md:p-6">
          <Card>
            <div className="text-center p-8">
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                <strong className="font-bold">Fel!</strong>
                <span className="block sm:inline"> {error}</span>
              </div>
            </div>
          </Card>
        </main>
      </div>
    );
  }

  const showYearNotice = Boolean(kpiData && globalFilters.year != null && latestYear < globalFilters.year);
  const showStudentCountNotice = Boolean(
    studentCountStatus &&
    studentCountStatus.missingForSelectedYear &&
    studentCountStatus.carryForwardFromYear != null
  );

  const dashboardContent = (
    <div className="space-y-8">
      {/* 1. SNABBÖVERSIKT (Full Width Hero) */}
      <section className="mb-2">
        <div className="flex items-center justify-between mb-4 px-1">
          <h2 className="text-base font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <span className="w-1.5 h-6 bg-emerald-500 rounded-full" />
            Nyckeltal i fokus
          </h2>
          <span className="text-xs text-slate-500">Mest relevanta indikatorer</span>
        </div>
        <KommunSummaryHero
          allKpiData={allKpiData}
          latestYear={latestYear}
          onKpiSelect={handleKpiSelect}
          loading={loading}
        />
      </section>

      <div className={isDesktop ? "grid grid-cols-1 lg:grid-cols-12 gap-8" : "space-y-8"}>
        {/* LEFT COLUMN: Results & Analysis (8/12) */}
        <div className={isDesktop ? "lg:col-span-8 space-y-8" : "space-y-8"}>

          {/* 2. RESULTAT (åk 9) */}
          <div className={isDesktop ? "col-span-12" : ""}>
            <SectionAccordion title="Resultat åk 9" defaultOpen grid={false} summary={<div className="text-sm text-muted-foreground">Viktigaste indikatorerna</div>}>
              <KpiGroupPanel
                group="ak9"
                kpiData={allKpiData}
                latestYear={latestYear}
                activeKpi={activeKpi}
                onKpiSelect={handleKpiSelect}
                compact={true}
                loading={loading}
              />
            </SectionAccordion>
          </div>

          {showSecondarySections && (
            <>
              {/* 2. RESULTAT (åk 6) */}
              <div className={isDesktop ? "col-span-12" : ""}>
                <SectionAccordion title="Resultat åk 6" grid={false} summary={<div className="text-sm text-muted-foreground">Nyckeltal åk 6</div>}>
                  <KpiGroupPanel
                    group="ak6"
                    kpiData={allKpiData}
                    latestYear={latestYear}
                    activeKpi={activeKpi}
                    onKpiSelect={handleKpiSelect}
                    compact={true}
                    loading={loading}
                  />
                </SectionAccordion>
              </div>

              {/* 3. RESURSER & STRUKTUR */}
              <div className={isDesktop ? "col-span-12" : ""}>
                <SectionAccordion title="Resurser & Struktur" grid={false} summary={<div className="text-sm text-muted-foreground">Lärarbehörighet, personaltäthet m.m.</div>}>
                  <KpiGroupPanel
                    group="resources"
                    kpiData={allKpiData}
                    latestYear={latestYear}
                    activeKpi={activeKpi}
                    onKpiSelect={handleKpiSelect}
                    compact={true}
                    loading={loading}
                  />
                </SectionAccordion>
              </div>

              {/* 4. ANALYS (Djupanalys) */}
              <div className={isDesktop ? "col-span-12" : ""}>
                <SectionAccordion title="Analys" defaultOpen grid={false}>
                  <section ref={deepDiveRef}>
                    <LazyAnalysisPanel
                      activeKpi={activeKpi}
                      kpiName={kpiMeta[activeKpi]?.name ?? 'Valt nyckeltal'}
                      latestYear={latestYear}
                      kommunId={globalFilters.kommunId}
                      skolaId={globalFilters.skolaId}
                      loading={loading}
                      trendData={trendData}
                      dynamicTrendData={dynamicTrendData}
                      distributionData={distributionData}
                      dynamicDistributionData={dynamicDistributionData}
                      driverData={driverData}
                      insightData={insightData}
                      correlationData={correlationData}
                      riketData={riketData}
                    />
                  </section>
                </SectionAccordion>
              </div>
            </>
          )}

          {!showSecondarySections && (
            <div className="h-40 flex items-center justify-center text-slate-400 text-sm">
              Laddar ytterligare sektioner...
            </div>
          )}
        </div>

        {/* RIGHT COLUMN: The Context Sidebar (4/12) */}
        <div className={isDesktop ? "lg:col-span-4" : "space-y-4"}>
          {isDesktop && (
            <div className="space-y-6 sticky top-24">
              {/* Use the new progressively disclosed "Insight Drawer" analysis */}
              {globalFilters.kommunId ? (
                <StatisticalAnalysisSummary
                  municipalityId={globalFilters.kommunId}
                  municipalityName={kommunName || 'Okänd'}
                  year={selectedYear}
                  kpiIds={[
                    'N15504', // Meritvärde
                    'N15418', // Betygskriterier
                    'N15436', // Behörighet yrkesprogram
                    'N15482', // Engelska E
                    'N15490', // Svenska E
                    'N15494', // Matematik E
                  ]}
                />
              ) : (
                <div className="bg-white dark:bg-slate-900 p-8 rounded-[2.5rem] shadow-sm border border-slate-200 dark:border-slate-800 text-center">
                  <p className="text-muted-foreground">Välj en kommun för att se analys</p>
                </div>
              )}

              {/* Additional context card for SALSA comparison if it's Meritvärde */}
              {activeKpi === 'N15504' && insightData && (
                <div className="bg-emerald-50 dark:bg-emerald-900/20 p-6 rounded-[2.5rem] border border-emerald-100 dark:border-emerald-800">
                  <h4 className="text-xs font-black text-emerald-800 dark:text-emerald-300 uppercase tracking-widest mb-2">
                    Jämförelse
                  </h4>
                  <div className="text-2xl font-black text-emerald-900 dark:text-emerald-100">
                    {insightData.latestAvvikelse > 0 ? '+' : ''}{insightData.latestAvvikelse.toFixed(1)} poäng
                  </div>
                  <div className="text-sm text-emerald-700 dark:text-emerald-400 font-medium">
                    {insightData.latestAvvikelse > 0 ? 'Över' : 'Under'} förväntat (SALSA)
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <main className={`w-full mx-auto p-4 md:p-6 relative ${isDesktop ? 'max-w-screen-2xl' : 'max-w-[1400px]'}`}>
        {loading && Object.keys(allKpiData).length === 0 && (
          <div className="fixed inset-0 bg-slate-50/20 dark:bg-slate-950/20 backdrop-blur-md z-50 flex items-center justify-center">
            <div className="p-8 rounded-3xl bg-white/40 dark:bg-slate-900/40 border border-white/20 dark:border-white/10 shadow-2xl flex flex-col items-center gap-6 max-w-sm text-center">
              <div className="text-emerald-500">
                <FancyLoader label={null} sizePx={80} />
              </div>
              <p className="text-slate-900 dark:text-white font-bold text-lg">Hämtar data för {kommunName}...</p>
            </div>
          </div>
        )}

        <HeroBanner
          kommunName={kommunName}
          selectedYear={selectedYear}
        />

        {showYearNotice && (
          <Card className="mb-6">
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <span className="text-amber-800">
                  Senaste tillgängliga data för denna kommun är från <strong>{latestYear}</strong>.
                  Siffrorna nedan avser det året.
                </span>
              </div>
            </div>
          </Card>
        )}

        {showStudentCountNotice && (
          <Card className="mb-6">
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-2">
                <svg className="w-5 h-5 text-amber-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <div className="text-amber-800">
                  <div>
                    Elevantal saknas för <strong>{studentCountStatus?.selectedYear}</strong>.
                    Elevviktade beräkningar använder senast tillgängliga elevantal från <strong>{studentCountStatus?.carryForwardFromYear}</strong>.
                  </div>
                  <div className="text-xs text-amber-700 mt-1">
                    (Detta påverkar endast delar av analysen som använder elevviktning.)
                  </div>
                </div>
              </div>
            </div>
          </Card>
        )}

        {Object.keys(allKpiData).length > 0 ? (
          dashboardContent
        ) : (
          <Card>
            <div className="p-6 text-center text-slate-500 dark:text-slate-400">Ingen KPI-data tillgänglig för denna kommun</div>
          </Card>
        )}

        <FullScreenModal
          open={isMobileSheetOpen}
          onOpenChange={(open) => setIsMobileSheetOpen(open)}
          activeKpi={selectedKpiId || ''}
          data={{
            latestYear,
            trendData,
            dynamicTrendData,
            distributionData,
            dynamicDistributionData,
            driverData,
            insightData,
            correlationData,
            riketData,
            loading
          }}
          contextInfo={{
            municipalityId: globalFilters.kommunId || undefined,
            municipalityName: kommunName || undefined,
            year: selectedYear,
            skolaId: globalFilters.skolaId ?? null
          }}
        />
      </main>
    </div>
  );
};

export default KommunDashboardView;
